import { useIntl } from 'react-intl';
import {
  Button,
  Col,
  Divider,
  Form,
  message,
  Row,
  Skeleton,
  Upload,
} from 'antd';

import { Helmet } from 'react-helmet';

import { PageProps } from '@/types/PageProps';

import { DefaultRouteTabs } from '@/components/_common';
import { useEffect, useState } from 'react';
import { useImportPurchases } from '@/core/domain/purchase/Purchase.hooks';
import { useHistory } from 'react-router-dom';
import { useCityMutation } from '@/core/domain/zipcode/Cities/Cities.hooks';
import { usePurchaseImportFileContext } from './PurchaseImportFileProvider';
import PurchaseImportDescriptions from '@/components/Movements/PurchaseImport/Descriptions';
import SpaceForButton from '@/components/_common/SpaceForButtons';

export interface PurchaseAddress {
  city?: string;
  state?: string;
}

export interface PurchaseImportPageFormValues {
  companyId?: number;
}

function PurchaseImportPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();

  const { setPurchaseImportData, setXml, setCompanyIdSelected } =
    usePurchaseImportFileContext();

  const [form] = Form.useForm<PurchaseImportPageFormValues>();

  const [file, setFile] = useState<File>();
  const [address, setAddress] = useState<PurchaseAddress>();

  const {
    data: importFileXmlData,
    mutateAsync,
    isLoading,
    reset,
  } = useImportPurchases();

  const cityMutation = useCityMutation();

  const handleFile = async () => {
    if (file) {
      await mutateAsync({ file });
    }
  };

  useEffect(() => {
    if (importFileXmlData) {
      if (
        importFileXmlData.supplierDTO.address?.cityId &&
        !(
          !!importFileXmlData.supplierDTO.address?.state &&
          !!importFileXmlData.supplierDTO.address?.city
        )
      ) {
        cityMutation.mutateAsync(importFileXmlData.supplierDTO.address.cityId);
      }

      setAddress({
        city: importFileXmlData.supplierDTO.address?.city ?? '',
        state: importFileXmlData.supplierDTO.address?.state ?? '',
      });

      setPurchaseImportData(importFileXmlData);
    }
  }, [importFileXmlData]);

  useEffect(() => {
    if (cityMutation.data) {
      const { name: city, state } = cityMutation.data;
      setAddress({ city, state });
    }
  }, [cityMutation.data]);

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        route={route.parent}
        permissions={route.permissions}
        extra={
          <Button
            block
            loading={isLoading}
            type="primary"
            onClick={handleFile}
            disabled={!file}
          >
            {intl.formatMessage({
              id: 'purchaseImport.form.message.import',
            })}
          </Button>
        }
      >
        <Row gutter={[16, 8]}>
          <Col span={24}>
            <Upload
              accept="text/xml"
              type="drag"
              name="file"
              maxCount={1}
              beforeUpload={(file) => {
                const isXML = file.type === 'text/xml';
                if (!isXML) {
                  message.error(`${file.name} não é um arquivo ".xml"`);
                }
                if (isXML) {
                  setXml(file);
                  setFile(file);
                  return false;
                }

                return isXML || Upload.LIST_IGNORE;
              }}
              onRemove={() => {
                setFile(undefined);
                reset();
              }}
            >
              {intl.formatMessage({
                id: 'purchaseImport.form.message.upload',
              })}
            </Upload>
          </Col>
        </Row>

        {importFileXmlData && address && (
          <Skeleton loading={cityMutation.isLoading}>
            <Divider style={file && { marginTop: 40 }} />

            <PurchaseImportDescriptions address={address} form={form} />

            <Divider />

            {file && (
              <SpaceForButton>
                <Button
                  block
                  type="primary"
                  onClick={async () => {
                    const values = await form.validateFields();

                    setCompanyIdSelected(values.companyId);

                    history.push(
                      '/app/movements/purchases/importPurchase/confirmImportPurchase',
                    );
                  }}
                >
                  Nova Compra
                </Button>

                <Button block type="primary" disabled>
                  Vincular a Compra existente
                </Button>
              </SpaceForButton>
            )}
          </Skeleton>
        )}
      </DefaultRouteTabs>
    </>
  );
}

export default PurchaseImportPage;
